import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useProviderSearch } from 'hooks/useProviderSearch';
import { CareProviderResults } from '@karehero/llama/pwa/organisms/CareProviderResults/CareProviderResults';
import Box from '@mui/material/Box';
import AdminProviderDetail, {
  ProviderNotesDialog,
} from '@karehero/llama/pwa/pages/AdminCareCircleView/AdminProviderDetail';
import { useGetProviderByRegulatoryIdQuery } from 'store/api/findCare';
import {
  useAddProviderNoteMutation,
  useDeleteProviderNoteMutation,
  useListProviderNotesQuery,
  useUpdateProviderNoteMutation,
} from 'store/api/providerNotes';

import {
  ActivityLogItem,
  CareProfileCategoryID,
  OrganiseEntryStatus,
  Shortlist,
  ShortlistItemStatus,
  SHORTLIST_ITEM_STATUS,
  isSystemTag,
} from '@karehero/models';
import {
  AdminCareCircleView as AdminCareCircleViewPage,
  AdminTab,
} from '@karehero/llama/pwa/pages/AdminCareCircleView/AdminCareCircleView';
import { useGoBack } from 'hooks/useGoBack';
import { track, TrackingEvent, useCareProfile, useTracking } from 'hooks';
import {
  useAddShortlistItemMutation,
  useArchiveShortlistMutation,
  useCreateShortlistMutation,
  useDismissShortlistItemMutation,
  useGetShortlistAdminProvidersQuery,
  useListShortlistsQuery,
  usePublishShortlistMutation,
  useRestoreShortlistItemMutation,
  useUpdateShortlistItemMutation,
  useUpdateShortlistMutation,
} from 'store/api/shortlist';
import {
  useGetAllQuery,
  useGetCareCircleQuery,
  useGetCareCircleActivityQuery,
} from 'store/api/careCircle';
import { useAdminCaseload } from 'hooks/useAdminCaseload';
import { newToast } from 'store/slices/uiSlice';
import { getAccount } from 'store/slices/iamSlice';

import { DYNAMIC_ROUTES } from 'router';
import EditEntryDialog, {
  EditEntryState,
} from '@karehero/llama/pwa/organisms/EntryDialog/EditEntryDialog';
import { useAdminOrganiserActions } from 'hooks/useAdminOrganiserActions';
import CreateEntryDialog from '@karehero/llama/pwa/organisms/EntryDialog/CreateEntryDialog';
import {
  AdminDocumentsPanel,
  type AdminDocumentItem,
  type DocumentUploadData,
} from '@karehero/llama/pwa/pages/AdminCareCircleView/AdminDocumentsPanel';
import {
  useListOrganiseDocumentsQuery,
  useListOrganiseDocumentTagsQuery,
  useLazyGetOrganiseDocumentQuery,
  useRequestOrganiseDocumentUploadURLMutation,
  useConfirmOrganiseDocumentUploadMutation,
  useDeleteOrganiseDocumentMutation,
} from 'store/api/organiseDocument';
import { memberNameMap } from '@karehero/models';

const categories = [
  CareProfileCategoryID.EligibilityCalculator,
  CareProfileCategoryID.CareAssessment,
  CareProfileCategoryID.FinancialStatus,
  CareProfileCategoryID.AttendanceAllowance,
];

const MAX_CARE_CIRCLES_PER_PAGE = 200;

interface OrganiserState {
  mode?: 'create' | 'edit' | null;
  entryId?: string | null;
}

// Wrapped for the admin page with a different consent text.
export const AdminCareCircleView = () => {
  const { careCircleID } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const account = useSelector(getAccount);
  const currentAccountId = account?.id ?? '';
  const { track: trackPostHog } = useTracking();

  const { data: careCircle } = useGetCareCircleQuery(careCircleID || '', {
    skip: !careCircleID,
  });

  const PAGE_SIZE = 20;
  const [activityOffset, setActivityOffset] = useState(0);
  const [allActivityItems, setAllActivityItems] = useState<ActivityLogItem[]>(
    [],
  );

  const {
    data: activityPage,
    isFetching: isActivityLoading,
    refetch: refetchActivity,
  } = useGetCareCircleActivityQuery(
    {
      careCircleId: careCircleID || '',
      limit: PAGE_SIZE,
      offset: activityOffset,
    },
    { skip: !careCircleID },
  );

  useEffect(() => {
    if (!activityPage) return;
    setAllActivityItems((prev) =>
      activityOffset === 0 ? activityPage : [...prev, ...activityPage],
    );
  }, [activityPage, activityOffset]);

  const hasMoreActivity = (activityPage?.length ?? 0) >= PAGE_SIZE;

  const handleLoadMoreActivity = useCallback(() => {
    setActivityOffset((prev) => prev + PAGE_SIZE);
  }, []);

  const handleRefreshActivity = useCallback(async () => {
    setActivityOffset(0);
    const { data } = await refetchActivity();
    setAllActivityItems(data ?? []);
  }, [refetchActivity]);

  const activityTabMap: Record<string, AdminTab> = {
    action_completed: AdminTab.Actions,
    form_completed: AdminTab.Forms,
    shortlist_item_secured: AdminTab.CareSearch,
    document_scanned: AdminTab.Overview,
  };

  const getActivityItemHref = useCallback(
    (item: ActivityLogItem) => {
      const ccId = item.careCircleId ?? careCircleID;
      if (!ccId) return undefined;
      const tab = activityTabMap[item.activityType];
      const base = DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(ccId);
      return tab ? `${base}?tab=${encodeURIComponent(tab)}` : base;
    },
    [careCircleID],
  );

  // Pull the first 200 care circles and filter client-side to those sharing
  // the same primary carer. Quick path; replace with a dedicated endpoint
  // (e.g. getCareCirclesByPrimaryCaregiver) once the data set grows.
  const { data: allCareCircles } = useGetAllQuery({
    perPage: MAX_CARE_CIRCLES_PER_PAGE,
  });

  const primaryCaregiverId = useMemo(
    () => careCircle?.primaryCaregiverAccount?.id,
    [careCircle],
  );

  const linkedCircles = useMemo(() => {
    if (!primaryCaregiverId || !allCareCircles?.data) return [];
    return allCareCircles.data
      .filter(
        (c) =>
          c.id !== careCircleID &&
          c.primaryCaregiverAccount?.id === primaryCaregiverId,
      )
      .map((c) => {
        const recipient = c.careRecipientAccount;
        const name =
          [recipient?.firstName, recipient?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() || 'Care circle';
        return {
          name,
          onClick: () => navigate(DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(c.id)),
        };
      });
  }, [allCareCircles, primaryCaregiverId, careCircleID, navigate]);

  const categoryData = categories.map((categoryID) => ({
    ...useCareProfile({ careCircleID, categoryID }),
    categoryID,
  }));

  // Provider search
  const {
    results: searchResults,
    isLoading: isSearchLoading,
    isLoadingMore: isSearchLoadingMore,
    hasMore: searchHasMore,
    loadMore: loadMoreSearchResults,
    search: searchProviders,
    setFilters: setSearchFilters,
    setCareType: setSearchCareType,
    setSort: setSearchSort,
    searchGeoPoint,
  } = useProviderSearch();

  const {
    hasCaseload,
    addToCaseload,
    removeFromCaseloadByCareCircle,
    makePrimary,
    getCaseStatus,
  } = useAdminCaseload();

  const caseStatus = useMemo(
    () => (careCircleID ? getCaseStatus(careCircleID) : null),
    [careCircleID, getCaseStatus],
  );
  const isOnCaseload = !!caseStatus;

  const handleAddToCaseload = useCallback(async () => {
    if (!careCircleID) return;
    await addToCaseload(careCircleID);
  }, [careCircleID, addToCaseload]);

  const handleRemoveFromCaseload = useCallback(async () => {
    if (!careCircleID) return;
    await removeFromCaseloadByCareCircle(careCircleID);
  }, [careCircleID, removeFromCaseloadByCareCircle]);

  const handleMakePrimary = useCallback(async () => {
    if (!careCircleID) return;
    await makePrimary(careCircleID);
  }, [careCircleID, makePrimary]);

  // URL-driven state (?tab=, ?shortlist=, ?provider=)
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProviderRegulatoryId = searchParams.get('provider');
  const selectedTab = (searchParams.get('tab') as AdminTab) || undefined;

  const handleTabChange = useCallback(
    (tab: AdminTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: selectedProvider } = useGetProviderByRegulatoryIdQuery(
    selectedProviderRegulatoryId || '',
    { skip: !selectedProviderRegulatoryId },
  );

  const handleProviderClick = useCallback(
    (regulatoryId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('provider', regulatoryId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleProviderBack = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('provider');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Global provider notes (visible to all CAMs, not tied to a shortlist)
  const [noteDialogRegulatoryId, setNoteDialogRegulatoryId] = useState<
    string | null
  >(null);

  const { data: detailProviderNotes } = useListProviderNotesQuery(
    selectedProviderRegulatoryId || '',
    { skip: !selectedProviderRegulatoryId },
  );
  const { data: dialogProviderNotes } = useListProviderNotesQuery(
    noteDialogRegulatoryId || '',
    { skip: !noteDialogRegulatoryId },
  );

  const noteDialogProviderName = useMemo(
    () =>
      searchResults?.find((p) => p.regulatoryId === noteDialogRegulatoryId)
        ?.name || '',
    [searchResults, noteDialogRegulatoryId],
  );

  const [addProviderNoteMutation] = useAddProviderNoteMutation();
  const [updateProviderNoteMutation] = useUpdateProviderNoteMutation();
  const [deleteProviderNoteMutation] = useDeleteProviderNoteMutation();

  const createNoteHandlers = useCallback(
    (regulatoryId: string) => ({
      onAddNote: async (text: string) => {
        const res = await addProviderNoteMutation({ regulatoryId, text });
        if ('error' in res) {
          Sentry.captureException(res.error);
          dispatch(
            newToast({
              title: 'Error',
              description: 'Failed to add note.',
            }),
          );
        }
      },
      onSaveNote: async (id: string, text: string) => {
        const res = await updateProviderNoteMutation({
          regulatoryId,
          noteId: id,
          text,
        });
        if ('error' in res) {
          Sentry.captureException(res.error);
          dispatch(
            newToast({
              title: 'Error',
              description: 'Failed to save note.',
            }),
          );
        }
      },
      onDeleteNote: async (id: string) => {
        const res = await deleteProviderNoteMutation({
          regulatoryId,
          noteId: id,
        });
        if ('error' in res) {
          Sentry.captureException(res.error);
          dispatch(
            newToast({
              title: 'Error',
              description: 'Failed to delete note.',
            }),
          );
        }
      },
    }),
    [
      addProviderNoteMutation,
      updateProviderNoteMutation,
      deleteProviderNoteMutation,
      dispatch,
    ],
  );

  // Shortlists (driven by ?shortlist= query param)

  const selectedShortlistID = searchParams.get('shortlist');
  const setSelectedShortlistID = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) {
            next.set('shortlist', id);
          } else {
            next.delete('shortlist');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: shortlists } = useListShortlistsQuery(careCircleID || '', {
    skip: !careCircleID,
  });

  const [onCreateShortlistMutation] = useCreateShortlistMutation();
  const [onUpdateShortlist] = useUpdateShortlistMutation();
  const [onArchiveShortlistMutation] = useArchiveShortlistMutation();
  const [onPublishShortlistMutation] = usePublishShortlistMutation();
  const [addItemMutation] = useAddShortlistItemMutation();
  const [updateItemMutation] = useUpdateShortlistItemMutation();
  const [dismissItemMutation] = useDismissShortlistItemMutation();
  const [restoreItemMutation] = useRestoreShortlistItemMutation();

  const { data: shortlistItems } = useGetShortlistAdminProvidersQuery(
    {
      careCircleID: careCircleID || '',
      shortlistID: selectedShortlistID || '',
    },
    { skip: !careCircleID || !selectedShortlistID },
  );

  const onAddToShortlist = useCallback(
    async (regulatoryId: string, shortlistIdOverride?: string) => {
      const shortlistId = shortlistIdOverride || selectedShortlistID;
      if (!careCircleID || !shortlistId) return;

      const res = await addItemMutation({
        careCircleID,
        shortlistID: shortlistId,
        regulatoryId,
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to add provider to shortlist.',
          }),
        );
        return;
      }

      const name = shortlists?.find((s) => s.id === shortlistId)?.name;
      track(TrackingEvent.ShortlistItemAdded, {
        careCircleID,
        shortlistID: shortlistId,
        regulatoryId,
      });
      dispatch(
        newToast({
          title: 'Added',
          description: `Added to ${name || 'shortlist'}.`,
        }),
      );
    },
    [careCircleID, selectedShortlistID, shortlists, addItemMutation, dispatch],
  );

  const onItemStatusChange = useCallback(
    async (regulatoryID: string, status: ShortlistItemStatus) => {
      if (!careCircleID || !selectedShortlistID) return;

      const params = {
        contacted: status === SHORTLIST_ITEM_STATUS.CONTACTED,
        secured: status === SHORTLIST_ITEM_STATUS.SECURED,
        unavailable: status === SHORTLIST_ITEM_STATUS.UNAVAILABLE,
      };

      await updateItemMutation({
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
        params,
      });
      track(TrackingEvent.ShortlistItemStatusChanged, {
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
        status,
      });
    },
    [careCircleID, selectedShortlistID, updateItemMutation],
  );

  const onItemDismiss = useCallback(
    async (regulatoryID: string, reason: string, detail?: string | null) => {
      if (!careCircleID || !selectedShortlistID) return;
      await dismissItemMutation({
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
        reason,
        detail: detail || undefined,
      });
      track(TrackingEvent.ShortlistItemDismissed, {
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
      });
    },
    [careCircleID, selectedShortlistID, dismissItemMutation],
  );

  const onItemRestore = useCallback(
    async (regulatoryID: string) => {
      if (!careCircleID || !selectedShortlistID) return;
      await restoreItemMutation({
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
      });
      track(TrackingEvent.ShortlistItemRestored, {
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
      });
    },
    [careCircleID, selectedShortlistID, restoreItemMutation],
  );

  const onItemNoteSave = useCallback(
    async (regulatoryID: string, note: string): Promise<boolean> => {
      if (!careCircleID || !selectedShortlistID) return false;
      const res = await updateItemMutation({
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
        params: { note },
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to save note.',
          }),
        );
        return false;
      }
      trackPostHog(TrackingEvent.ShortlistItemNoteSaved, {
        careCircleID,
        shortlistID: selectedShortlistID,
        regulatoryID,
      });
      return true;
    },
    [
      careCircleID,
      selectedShortlistID,
      updateItemMutation,
      trackPostHog,
      dispatch,
    ],
  );

  // Organisers
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialog, setDialog] = useState<OrganiserState>({});

  const {
    isPerformingAction,
    entries,
    calendars,
    findRawEntry,
    createEntry,
    updateEntry,
    deleteEntry,
    cancelOccurence,
    deleteRepeatingTask,
    changeStatus,
  } = useAdminOrganiserActions(careCircleID || '', {
    skip: !careCircleID,
  });

  const entry = useMemo(() => {
    if (!dialog.entryId) return;
    return findRawEntry(dialog.entryId);
  }, [findRawEntry, dialog.entryId]);

  // Handlers
  const handleEntryClick = useCallback(
    (entryId: string) => {
      const entry = findRawEntry(entryId);
      if (!entry) return;

      track(TrackingEvent.OrganiserDialogOpened, {
        mode: 'edit',
        isRecurring: !!entry.recurringEventId,
      });
      setDialog({
        mode: 'edit',
        entryId: entryId,
      });
    },
    [findRawEntry, track],
  );

  const handleAddEntry = useCallback(() => {
    track(TrackingEvent.OrganiserDialogOpened, { mode: 'create' });

    setDialog({
      mode: 'create',
    });
  }, [track]);

  const handleDialogComplete = useCallback(() => setDialog({}), [setDialog]);

  const handleDialogCancelled = useCallback(() => {
    track(TrackingEvent.OrganiserDialogCancelled, {
      mode: dialog.mode,
    });
    setDialog({});
  }, [track]);

  const handleStatusChange = useCallback(
    async (entryId: string, status: OrganiseEntryStatus) => {
      await changeStatus(entryId, status);
    },
    [changeStatus],
  );

  const handleUpdate = useCallback(
    async (entry: EditEntryState) => {
      if (!entry.title.trim() || !entry.calendarId) return;

      await updateEntry(entry);

      handleDialogComplete();
    },
    [updateEntry, handleDialogComplete],
  );

  const handleCreate = useCallback(
    async (entry: any) => {
      if (!entry.title.trim() || !entry.calendarId) return;

      await createEntry(entry);

      handleDialogComplete();
    },
    [createEntry, handleDialogComplete],
  );

  const handleDelete = useCallback(
    async (calendarId: string, entryId: string) => {
      if (!entryId || !calendarId) return;

      await deleteEntry(calendarId, entryId);

      handleDialogComplete();
    },
    [deleteEntry, handleDialogComplete],
  );

  const handleCancelOccurrence = useCallback(
    async (
      calendarId: string,
      recurringEventId?: string | null,
      occurrenceDate?: string | null,
    ) => {
      if (!recurringEventId || !occurrenceDate) return;

      await cancelOccurence(calendarId, recurringEventId, occurrenceDate);

      handleDialogComplete();
    },
    [cancelOccurence, handleDialogComplete],
  );

  const handleDeleteRepeatingTask = useCallback(
    async (calendarId: string, recurringEventId?: string | null) => {
      if (!recurringEventId) return;

      await deleteRepeatingTask(calendarId, recurringEventId);

      handleDialogComplete();
    },
    [deleteRepeatingTask, handleDialogComplete],
  );

  const onToggleShowCompleted = useCallback(() => {
    setShowCompleted((prev) => {
      track(TrackingEvent.OrganiserHideCompletedToggled, {
        isHidden: prev,
      });
      return !prev;
    });
  }, [setShowCompleted, track]);

  const onShowMore = useCallback(
    (currentlyVisible: number, totalUpcoming: number) => {
      track(TrackingEvent.OrganiserShowMoreClicked, {
        currentlyVisible,
        totalUpcoming,
      });
    },
    [track],
  );

  const onCreateShortlist = useCallback(
    async (params: { careCircleID: string; shortlist: Shortlist }) => {
      const res = await onCreateShortlistMutation(params);
      if (!('data' in res)) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to create shortlist.',
          }),
        );
        return;
      }
      return res.data;
    },
    [onCreateShortlistMutation, dispatch],
  );

  const onArchiveShortlist = useCallback(
    async (params: { careCircleID: string; id: string }) => {
      const res = await onArchiveShortlistMutation(params);
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to archive shortlist.',
          }),
        );
        return;
      }

      dispatch(
        newToast({
          title: 'Success',
          description: 'Shortlist has been archived.',
        }),
      );
    },
    [onArchiveShortlistMutation, dispatch],
  );

  const onPublishShortlist = useCallback(
    async (params: { careCircleID: string; id: string }) => {
      const res = await onPublishShortlistMutation(params);
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to publish shortlist.',
          }),
        );
        return;
      }

      dispatch(
        newToast({
          title: 'Success',
          description: 'Shortlist has been published.',
        }),
      );
    },
    [onPublishShortlistMutation, dispatch],
  );

  // Documents
  const { data: documents = [], isLoading: isLoadingDocs } =
    useListOrganiseDocumentsQuery(
      { careCircleId: careCircleID || '' },
      { skip: !careCircleID },
    );

  const { data: allDocumentTags = [] } = useListOrganiseDocumentTagsQuery(
    careCircleID || '',
    { skip: !careCircleID },
  );
  const documentTags = useMemo(
    () => allDocumentTags.filter((tag) => !isSystemTag(tag.id)),
    [allDocumentTags],
  );

  const [getDocument] = useLazyGetOrganiseDocumentQuery();
  const [requestUploadURL] = useRequestOrganiseDocumentUploadURLMutation();
  const [confirmUpload] = useConfirmOrganiseDocumentUploadMutation();
  const [deleteDocumentMutation] = useDeleteOrganiseDocumentMutation();
  const [isUploading, setIsUploading] = useState(false);

  const uploaderNames = useMemo(() => {
    const map = memberNameMap(careCircle?.careCircleMembers ?? []);
    if (account?.id) map[account.id] = 'You';
    return map;
  }, [account, careCircle]);

  const documentItems: AdminDocumentItem[] = useMemo(
    () =>
      documents.map((doc) => ({
        id: doc.id,
        title: doc.title,
        filename: doc.filename,
        sizeBytes: doc.sizeBytes,
        status: doc.status,
        tags: doc.tags,
        createdAt: doc.createdAt,
        uploaderName: uploaderNames[doc.uploaderAccountId],
      })),
    [documents, uploaderNames],
  );

  const handleDocumentUpload = useCallback(
    async (data: DocumentUploadData) => {
      if (!careCircleID) return;
      setIsUploading(true);
      try {
        const response = await requestUploadURL({
          careCircleId: careCircleID,
          body: {
            title: data.title,
            description: data.description,
            filename: data.file.name,
            contentType: data.file.type,
            sizeBytes: data.file.size,
            tags: data.tags.length > 0 ? data.tags : undefined,
          },
        }).unwrap();

        const { documentId, uploadUrl } = response;
        if (!uploadUrl) throw new Error('No upload URL in response');

        const isJSONUploadEndpoint = uploadUrl.includes('/upload/storage/');
        const uploadResponse = await fetch(uploadUrl, {
          method: isJSONUploadEndpoint ? 'POST' : 'PUT',
          body: data.file,
          headers: { 'Content-Type': data.file.type },
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${await uploadResponse.text()}`);
        }

        await confirmUpload({
          careCircleId: careCircleID,
          body: { documentId },
        }).unwrap();

        dispatch(
          newToast({
            title: 'Document uploaded',
            description: 'Your document has been uploaded successfully.',
          }),
        );
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Upload failed',
            description:
              error instanceof Error
                ? error.message
                : 'An error occurred while uploading.',
          }),
        );
      } finally {
        setIsUploading(false);
      }
    },
    [careCircleID, requestUploadURL, confirmUpload, dispatch],
  );

  const handleDocumentDownload = useCallback(
    async (doc: AdminDocumentItem) => {
      if (!careCircleID) return;
      try {
        const result = await getDocument({
          careCircleId: careCircleID,
          documentId: doc.id,
        }).unwrap();
        window.open(result.downloadUrl, '_blank');
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Download failed',
            description: 'Could not download the document.',
          }),
        );
      }
    },
    [careCircleID, getDocument, dispatch],
  );

  const handleDocumentDelete = useCallback(
    async (doc: AdminDocumentItem) => {
      if (!careCircleID) return;
      try {
        await deleteDocumentMutation({
          careCircleId: careCircleID,
          documentId: doc.id,
        }).unwrap();
        dispatch(
          newToast({
            title: 'Document deleted',
            description: `"${doc.title}" has been deleted.`,
          }),
        );
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Delete failed',
            description: 'Could not delete the document.',
          }),
        );
      }
    },
    [careCircleID, deleteDocumentMutation, dispatch],
  );

  return (
    <>
      <AdminCareCircleViewPage
        onBack={goBack}
        careCircle={careCircle}
        categoryData={categoryData}
        linkedCircles={linkedCircles}
        entries={entries}
        onStatusChange={handleStatusChange}
        onEntryClick={handleEntryClick}
        onAddEntry={handleAddEntry}
        onEntryLinkClick={(link) => navigate(link)}
        showCompleted={showCompleted}
        onToggleShowCompleted={onToggleShowCompleted}
        onShowMore={onShowMore}
        shortlists={shortlists}
        onCreateShortlist={onCreateShortlist}
        onUpdateShortlist={onUpdateShortlist}
        onArchiveShortlist={onArchiveShortlist}
        onPublishShortlist={onPublishShortlist}
        shortlistID={selectedShortlistID}
        setShortlistID={setSelectedShortlistID}
        shortlistItems={shortlistItems}
        onItemStatusChange={onItemStatusChange}
        onItemDismiss={onItemDismiss}
        onItemRestore={onItemRestore}
        onItemClick={handleProviderClick}
        onItemNoteSave={onItemNoteSave}
        documentsContent={
          <AdminDocumentsPanel
            documents={documentItems}
            availableTags={documentTags}
            isLoading={isLoadingDocs}
            isUploading={isUploading}
            onUpload={handleDocumentUpload}
            onDownload={handleDocumentDownload}
            onDelete={handleDocumentDelete}
          />
        }
        tab={selectedTab}
        onTabChange={handleTabChange}
        onAddToCaseload={
          hasCaseload && !isOnCaseload ? handleAddToCaseload : undefined
        }
        onRemoveFromCaseload={
          hasCaseload && isOnCaseload ? handleRemoveFromCaseload : undefined
        }
        onMakePrimary={
          hasCaseload && isOnCaseload && !caseStatus?.isPrimary
            ? handleMakePrimary
            : undefined
        }
        caseManager={careCircle?.caseManager}
        watchers={careCircle?.watchers}
        activityItems={allActivityItems}
        isActivityLoading={isActivityLoading}
        hasMoreActivity={hasMoreActivity}
        onLoadMoreActivity={handleLoadMoreActivity}
        onRefreshActivity={handleRefreshActivity}
        onActivityCareCircleClick={(id) =>
          navigate(DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(id))
        }
        getActivityItemHref={getActivityItemHref}
        onActivityItemClick={(item) => {
          const href = getActivityItemHref(item);
          if (href) navigate(href);
        }}
        careSearchContent={({ onOpenCreateShortlistForm }) => (
          <>
            {selectedProvider && selectedProviderRegulatoryId && (
              <AdminProviderDetail
                provider={selectedProvider}
                notes={detailProviderNotes || []}
                currentUser={currentAccountId}
                shortlists={shortlists}
                selectedShortlistID={selectedShortlistID}
                onAddToShortlist={(shortlistId) =>
                  onAddToShortlist(selectedProviderRegulatoryId, shortlistId)
                }
                onCreateShortlist={onOpenCreateShortlistForm}
                {...createNoteHandlers(selectedProviderRegulatoryId)}
                onBack={handleProviderBack}
              />
            )}
            <Box
              sx={{
                display: selectedProviderRegulatoryId ? 'none' : 'block',
              }}
            >
              <CareProviderResults
                apiKey={import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                searchLocation={searchGeoPoint}
                results={searchResults}
                isLoading={isSearchLoading}
                hasMore={searchHasMore}
                isLoadingMore={isSearchLoadingMore}
                onLoadMore={loadMoreSearchResults}
                onSearch={(location) => {
                  searchProviders(location);
                  track(TrackingEvent.ShortlistProviderSearched, {
                    careCircleID,
                    location,
                  });
                }}
                shortlists={shortlists}
                selectedShortlistID={selectedShortlistID}
                onAddToShortlist={onAddToShortlist}
                onCreateShortlist={onOpenCreateShortlistForm}
                onFilterChange={setSearchFilters}
                onCareTypeChange={setSearchCareType}
                onSortChange={setSearchSort}
                onProviderClick={handleProviderClick}
                onAddNote={(regulatoryId) =>
                  setNoteDialogRegulatoryId(regulatoryId)
                }
              />
            </Box>
            {noteDialogRegulatoryId && (
              <ProviderNotesDialog
                isOpen
                onClose={() => setNoteDialogRegulatoryId(null)}
                providerName={noteDialogProviderName}
                notes={dialogProviderNotes || []}
                currentUser={currentAccountId}
                {...createNoteHandlers(noteDialogRegulatoryId)}
              />
            )}
          </>
        )}
      />
      {dialog.mode === 'edit' && entry ? (
        <EditEntryDialog
          isOpen={dialog.mode === 'edit'}
          onClose={handleDialogCancelled}
          entry={entry}
          calendars={calendars}
          onSave={handleUpdate}
          onDelete={handleDelete}
          onCancelOccurrence={handleCancelOccurrence}
          onDeleteSeries={handleDeleteRepeatingTask}
          isSaving={isPerformingAction}
        />
      ) : (
        <CreateEntryDialog
          isOpen={dialog.mode === 'create'}
          onClose={handleDialogCancelled}
          calendars={calendars}
          onSave={handleCreate}
          isSaving={isPerformingAction}
        />
      )}
    </>
  );
};

export default AdminCareCircleView;
